import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiFetch } from "../utils/api.js";
import dayjs from "dayjs";
import { SUBSCRIPTION_NAMES } from "../utils/consts.js";
import { classifyError, isApiError } from "../utils/api-error.js";
import { logout, fetchSession } from "./auth";

// apiFetch throws ApiError on any non-2xx, so the thunks just unwrap
// the JSON; fetchSubscription additionally rejects with { status } so
// its reducer can tell authoritative failures from blips.

export const checkUserData = createAsyncThunk("user/userData", async () => {
    const response = await apiFetch("/v2/user/@me");
    return response.json();
});

export const fetchPurchases = createAsyncThunk("user/purchases", async () => {
    const response = await apiFetch("/v2/organisation/purchases");
    return response.json();
});

export const fetchSubscription = createAsyncThunk(
    "user/fetchSubscription",
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiFetch("/v2/organisation/subscription");
            return await response.json();
        } catch (err) {
            // apiFetch throws ApiError on any non-2xx; JSON error bodies
            // make the message body text and RTK's miniSerializeError
            // drops .status, so the status travels in the payload
            // instead (review finding I2). Status 0 (network, timeout,
            // abort) maps to the transient "network" class so a blip
            // keeps last-good data (review finding I1), mirroring auth's
            // statusFromError.
            return rejectWithValue({
                status: isApiError(err)
                    ? (err.status || "network")
                    : "unknown",
            });
        }
    },
);

export const fetchCredits = createAsyncThunk("user/fetchCredits", async () => {
    const response = await apiFetch("/v2/organisation/credits");
    return response.json();
});

export const fetchOrgStats = createAsyncThunk("user/fetchOrgStats", async () => {
    const response = await apiFetch("/v2/survey?stats=2");
    return response.json();
});

export const updateUserStatus = createAsyncThunk(
    "user/updateStatus",
    async ({ userId, active, inactive_reason }) => {
        const response = await apiFetch(`/v2/user/${userId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ active, inactive_reason }),
        });
        return response.json();
    }
);

// stripe_item.price_id for the managed plan. The row is configured out-of-band
// (directly in the database, not via deploy SQL), so this literal mirrors it.
const MANAGED_PRICE_ID = "price_MANAGED";

const findManagedPurchase = (purchases) => (
    Array.isArray(purchases) ? purchases.find((p) => p?.item_id === MANAGED_PRICE_ID) : null
);

// GET /organisation/purchases needs the "Organisation admin"
// permission (main admins bypass every assert). Shared by the login
// bootstrap and the payment return so the request only fires for users
// who can read it.
export const canReadPurchases = (userData) => Boolean(
    userData?.is_main_admin ||
        Object.values(userData?.all_permissions || {}).includes("Organisation admin"),
);

export const isManagedAccount = (purchases, subscriptionData) => (
    Boolean(findManagedPurchase(purchases)) || subscriptionData?.item_id === MANAGED_PRICE_ID
);

// A complete, unexpired subscription on the subscription endpoint means
// the organisation is subscribed, whatever the viewer's role: members
// can read that endpoint ("See credit balance" tier) but never the
// purchases behind it, so their derivations fall back to
// subscriptionData instead of reporting a free plan (issue #1129).
const subscriptionIsActive = (subscriptionData) => (
    subscriptionData?.status === "complete" &&
    dayjs(subscriptionData?.expires).isValid() &&
    dayjs(subscriptionData?.expires).isAfter(dayjs())
);

const calculateActiveSubscription = (userData, purchases, subscriptionData) => {
    const isMainAdmin = userData?.is_main_admin;

    if (isManagedAccount(purchases, subscriptionData)) {
        const managed = findManagedPurchase(purchases);
        if (!managed?.expires) return true;
        return dayjs(managed.expires).isValid() && dayjs(managed.expires).isAfter(dayjs());
    }

    if (!isMainAdmin) {
        // Purchase rows are admin-only; members resolve through the
        // subscription endpoint.
        return subscriptionIsActive(subscriptionData);
    }

    if (!purchases || purchases.length === 0) return false;
    const recurringPurchase = purchases.find((product) => product?.expires);
    const expiryDate = recurringPurchase?.expires;
    if (expiryDate && dayjs(expiryDate).isValid() && dayjs(expiryDate).isAfter(dayjs())) {
        return true;
    }
    return false;
};

const calculateSubscriptionDetails = (userData, purchases, subscriptionData) => {
    const isMainAdmin = userData?.is_main_admin;

    if (isManagedAccount(purchases, subscriptionData)) {
        const managed = findManagedPurchase(purchases);
        return managed?.description || "Managed Account";
    }

    if (!isMainAdmin) {
        // Members derive the plan name from the subscription's renewal
        // period; purchase descriptions stay admin-only.
        if (subscriptionIsActive(subscriptionData)) {
            // Only the two seat-subscription periods have canonical
            // names; anything else ("never" credit packs, NULL) must
            // not silently label as Yearly.
            if (subscriptionData.renews_after === "month") {
                return SUBSCRIPTION_NAMES.MONTHLY;
            }
            if (subscriptionData.renews_after === "year") {
                return SUBSCRIPTION_NAMES.YEARLY;
            }
        }
        return SUBSCRIPTION_NAMES.FREE_INDIVIDUAL;
    }

    if (!purchases || purchases.length === 0) return SUBSCRIPTION_NAMES.FREE_INDIVIDUAL;
    const recurringPurchase = purchases.find((purchase) => purchase?.expires);
    if (recurringPurchase && recurringPurchase.expires && dayjs(recurringPurchase.expires).isValid() && dayjs(recurringPurchase.expires).isAfter(dayjs())) {
        return recurringPurchase?.description || "Unknown Subscription";
    }
    return SUBSCRIPTION_NAMES.FREE_INDIVIDUAL;
};

/**
 * Helper function to check if user has the "Account Holder" role
 * @param {object} userData - The user data object containing roles
 * @returns {boolean} True if user has Account Holder role
 */
const calculateIsAccountHolder = (userData) => {
    return Object.values(userData?.roles || {}).includes("Account Holder");
};

export const userSlice = createSlice({
    name: "user",
    initialState: {
        userData: false,
        userDataError: null,
        purchases: [],
        purchasesError: null,
        activeSubscription: false,
        subscriptionDetails: SUBSCRIPTION_NAMES.FREE_INDIVIDUAL,
        subscriptionData: null,
        subscriptionError: null,
        isAccountHolder: false,
        isManaged: false,
        creditsData: null,
        creditsError: null,
        orgStats: null,
        orgStatsError: null,
        // Cache-busting key for every profile picture URL consumer
        // (dialog, page displays, navbar avatar). Seeded per store load
        // so each page session starts with a unique URL, bumped by
        // bumpPictureVersion after every successful picture change.
        pictureVersion: Date.now(),
    },
    reducers: {
        bumpPictureVersion: (state) => {
            state.pictureVersion += 1;
        },
    },
    extraReducers(builder) {
        // An in-tab logout or session expiry leaves stale user data in
        // the store for the next login's gates (purchases, derivations)
        // to read (review finding I3). pictureVersion keeps counting up
        // so two users in one SPA session never share an avatar URL.
        const resetForNewSession = (state) => ({
            ...userSlice.getInitialState(),
            pictureVersion: state.pictureVersion + 1,
        });
        builder.addCase(logout.fulfilled, resetForNewSession);
        // fetchSession rejects with "session-expired" only for 401s;
        // network failures carry no payload and must not reset.
        builder.addCase(fetchSession.rejected, (state, action) => {
            if (action.payload === "session-expired") {
                return resetForNewSession(state);
            }
        });
        // Check User Async States
        builder.addCase(checkUserData.pending, (state) => {
            state.userData = false;
            state.isAccountHolder = false;
            state.userDataError = null;
        });
        builder.addCase(checkUserData.fulfilled, (state, action) => {
            state.userData = action.payload;
            state.isManaged = isManagedAccount(state.purchases, state.subscriptionData);
            state.activeSubscription = calculateActiveSubscription(action.payload, state.purchases, state.subscriptionData);
            state.subscriptionDetails = calculateSubscriptionDetails(action.payload, state.purchases, state.subscriptionData);
            state.isAccountHolder = calculateIsAccountHolder(action.payload);
            state.userDataError = null;
        });
        builder.addCase(checkUserData.rejected, (state, action) => {
            state.userData = false;
            state.isManaged = false;
            state.activeSubscription = false;
            state.subscriptionDetails = SUBSCRIPTION_NAMES.FREE_INDIVIDUAL;
            state.isAccountHolder = false;
            state.userDataError = classifyError(action.error) ?? null;
        });
        // Fetch Purchases Async States
        builder.addCase(fetchPurchases.pending, (state) => {
            state.purchases = [];
            state.purchasesError = null;
        });
        builder.addCase(fetchPurchases.fulfilled, (state, action) => {
            state.purchases = action.payload;
            state.isManaged = isManagedAccount(action.payload, state.subscriptionData);
            state.activeSubscription = calculateActiveSubscription(state.userData, action.payload, state.subscriptionData);
            state.subscriptionDetails = calculateSubscriptionDetails(state.userData, action.payload, state.subscriptionData);
            state.purchasesError = null;
        });
        builder.addCase(fetchPurchases.rejected, (state, action) => {
            state.purchases = [];
            state.isManaged = isManagedAccount([], state.subscriptionData);
            // Recompute through the derivations rather than hardcoding
            // the free plan: a member (or a delegated admin mid-session
            // role change) keeps whatever live subscriptionData says.
            state.activeSubscription = calculateActiveSubscription(state.userData, [], state.subscriptionData);
            state.subscriptionDetails = calculateSubscriptionDetails(state.userData, [], state.subscriptionData);
            state.purchasesError = classifyError(action.error) ?? null;
        });
        // Fetch Subscription Async States
        builder.addCase(fetchSubscription.pending, (state) => {
            state.subscriptionError = null;
        });
        builder.addCase(fetchSubscription.fulfilled, (state, action) => {
            const subscription = action.payload;
            // The API returns an empty object if there is no subscription.
            if (subscription && Object.keys(subscription).length > 0) {
                state.subscriptionData = subscription;
                state.isManaged = isManagedAccount(state.purchases, subscription);
                state.activeSubscription = calculateActiveSubscription(state.userData, state.purchases, subscription);
                state.subscriptionDetails = calculateSubscriptionDetails(state.userData, state.purchases, subscription);
            } else {
                state.subscriptionData = null;
                state.isManaged = isManagedAccount(state.purchases, null);
                state.activeSubscription = calculateActiveSubscription(state.userData, state.purchases, null);
                state.subscriptionDetails = calculateSubscriptionDetails(state.userData, state.purchases, null);
            }
            state.subscriptionError = null;
        });
        builder.addCase(fetchSubscription.rejected, (state, action) => {
            // The thunk rejects with { status } (review finding I2):
            // serialized action.error has neither a .status nor an
            // "HTTP NNN" message for real JSON error bodies. Transient
            // failures (network, abort, unclassifiable) keep the
            // last-good data -- members' whole plan view derives from it
            // and a blip must not blank it. Anything with an HTTP
            // status is authoritative and clears it.
            const status = action.payload?.status ?? classifyError(action.error);
            const keepLastGood =
                status === "unknown" || status === "network";
            if (!keepLastGood) {
                state.subscriptionData = null;
            }
            const subscriptionData = state.subscriptionData;
            state.isManaged = isManagedAccount(state.purchases, subscriptionData);
            state.activeSubscription = calculateActiveSubscription(state.userData, state.purchases, subscriptionData);
            state.subscriptionDetails = calculateSubscriptionDetails(state.userData, state.purchases, subscriptionData);
            state.subscriptionError = status ?? null;
        });
        // Fetch Credits Async States
        builder.addCase(fetchCredits.pending, (state) => {
            state.creditsError = null;
        });
        builder.addCase(fetchCredits.fulfilled, (state, action) => {
            state.creditsData = action.payload;
            state.creditsError = null;
        });
        builder.addCase(fetchCredits.rejected, (state, action) => {
            state.creditsData = null;
            state.creditsError = classifyError(action.error) ?? null;
        });
        // Fetch Org Stats Async States
        builder.addCase(fetchOrgStats.pending, (state) => {
            state.orgStatsError = null;
        });
        builder.addCase(fetchOrgStats.fulfilled, (state, action) => {
            state.orgStats = action.payload;
            state.orgStatsError = null;
        });
        builder.addCase(fetchOrgStats.rejected, (state, action) => {
            state.orgStats = null;
            state.orgStatsError = classifyError(action.error) ?? null;
        });
    },
});

export const { bumpPictureVersion } = userSlice.actions;

export const userSelector = (state) => state[userSlice.name];

export default userSlice.reducer;
